import { Card, Colors, Heading, Icon, Text, type ColorType } from '@amelio/ui-library';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CompetencyItem } from './CompetencyItem';

import type { ProficiencyLevel } from '@/types/competencies/ProficiencyLevel';
import type { CompetencyGroupResult } from '@/types/manager-assessment/Competency';
import type { CompetencyResult } from '@/types/review/CompetencyGroupResult';

import { CustomPill } from '@/components/CustomPill/CustomPill';
import { useSelectedAppLanguage } from '@/hooks';
import { getLocalizedText, getTextByLanguage } from '@/utils/localization';
import { orderProficiencyLevels } from '@/utils/proficiencyLevel';

interface CompetencyCategoryProps {
    group: CompetencyGroupResult;
    isOpen: boolean;
    employeeId?: string;
    proficiencyLevels: ProficiencyLevel[];
    isReadOnly?: boolean;
    onToggleCategory: (categoryId: string) => void;
    onHistoryClick: (competency: CompetencyResult) => void;
    onEditClick: (competency: CompetencyResult, e: React.MouseEvent) => void;
}

export const CompetencyGroupAccordion = ({
    group,
    isOpen,
    employeeId,
    proficiencyLevels,
    isReadOnly,
    onToggleCategory,
    onHistoryClick,
    onEditClick,
}: CompetencyCategoryProps) => {
    const { t } = useTranslation();
    const selectedLanguage = useSelectedAppLanguage();

    const orderedLevels = useMemo(
        () => orderProficiencyLevels(proficiencyLevels),
        [proficiencyLevels],
    );

    const groupProficiencyLevel = useMemo(() => {
        const levelIndexes = group.competencies
            .filter((competency) => !competency.isDeactivated && !competency.isDeleted)
            .map(
                (competency) =>
                    competency.proficiencyLevel ??
                    (competency.lastAssessedProficiencyLevelId
                        ? proficiencyLevels.find(
                              (level) => level.id === competency.lastAssessedProficiencyLevelId,
                          )
                        : undefined),
            )
            .map((level) => orderedLevels.findIndex((l) => l.id === level?.id))
            .filter((index) => index >= 0);

        if (levelIndexes.length === 0) {
            return null;
        }

        const averageIndex = Math.round(
            levelIndexes.reduce((sum, index) => sum + index, 0) / levelIndexes.length,
        );
        return orderedLevels[averageIndex];
    }, [group.competencies, proficiencyLevels, orderedLevels]);

    const handleToggle = () => {
        onToggleCategory(group.id);
    };

    return (
        <Card
            key={group.id}
            padding={'none'}
            className={`!rounded-[12px] ${isOpen ? '!border !border-gray-200' : ''} overflow-hidden`}
            borderRadius={'2xl'}
        >
            <button
                type={'button'}
                onClick={handleToggle}
                className={`focus-visible:ring-primary-800 bg-gradient-pastel-cool flex w-full items-center justify-between gap-3 overflow-hidden rounded-t-[12px] px-[16px] py-[12px] transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${isOpen ? '!border-0 !border-b !border-gray-200' : 'border border-gray-100'}`}
                aria-expanded={isOpen}
            >
                <div className={'flex min-w-0 items-center gap-3'}>
                    <Heading
                        level={4}
                        weight={'semibold'}
                        color={Colors.NAVY}
                        className={'text-font-lg!'}
                    >
                        {group.name[selectedLanguage] ||
                            getTextByLanguage(group.name || '', selectedLanguage)}
                    </Heading>
                    {groupProficiencyLevel ? (
                        <CustomPill color={groupProficiencyLevel.iconColor as ColorType}>
                            {getLocalizedText(groupProficiencyLevel.label)}
                        </CustomPill>
                    ) : (
                        <Text
                            size={'default'}
                            color={Colors.BASE_400}
                        >
                            {t('common.notDefined')}
                        </Text>
                    )}
                </div>

                <Icon
                    name={isOpen ? 'arrow-arrow-up-1' : 'arrow-arrow-down'}
                    size={'sm'}
                    color={Colors.BASE_600}
                />
            </button>

            <div
                className={'overflow-hidden transition-all duration-300 ease-in-out'}
                style={{
                    maxHeight: isOpen ? '1000px' : '0px',
                    opacity: isOpen ? 1 : 0,
                }}
            >
                <div>
                    {group.competencies.map((competency) => (
                        <CompetencyItem
                            key={competency.id}
                            competency={competency}
                            employeeId={employeeId}
                            proficiencyLevels={proficiencyLevels}
                            isReadOnly={isReadOnly}
                            onHistoryClick={onHistoryClick}
                            onEditClick={onEditClick}
                        />
                    ))}
                </div>
            </div>
        </Card>
    );
};

CompetencyGroupAccordion.displayName = 'CompetencyGroupAccordion';
